/**
 * Shared consent state and tracking-script management.
 *
 * Consent model (opt-in):
 *   - Default (no cookie/localStorage) → scripts BLOCKED, notice shown
 *   - "accepted" → scripts load, notice hidden
 *   - "declined" → scripts blocked, notice hidden
 *   - GPC active → always treated as "declined"
 *
 * Single source of truth is the `vh_consent` first-party cookie (1 year TTL).
 * localStorage mirrors the cookie for fast synchronous reads at page load.
 *
 * Scripts managed here:
 *   • Smartlook  — session recording / analytics
 *   • Google Ads (AW-17684009574) — conversion tracking + remarketing
 *
 * Scripts NOT managed here (always load):
 *   • Intercom   — customer support service provider
 *   • Sender.net — newsletter signup, not behavioral advertising
 */

export const CONSENT_COOKIE = "vh_consent";
export const CONSENT_KEY = "voltHubCookieConsent";
const CONSENT_TTL_DAYS = 365;

export type ConsentState = "accepted" | "declined" | null;

// ─── Cookie helpers ───────────────────────────────────────────────────────────

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number) {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Returns whether GPC is signalling an opt-out. */
export function isGpcActive(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

/**
 * Reads the current consent state.
 * GPC always overrides stored preferences.
 */
export function getConsent(): ConsentState {
  if (isGpcActive()) return "declined";

  // Cookie is the primary source of truth
  const cookie = readCookie(CONSENT_COOKIE);
  if (cookie === "accepted" || cookie === "declined") return cookie as ConsentState;

  // localStorage as fallback (e.g. cookie blocked by browser)
  if (typeof localStorage !== "undefined") {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored === "accepted" || stored === "declined") return stored as ConsentState;
  }

  return null; // no preference set yet — show notice
}

/** Persists an explicit consent choice (1-year cookie + localStorage). */
export function setConsent(state: "accepted" | "declined"): void {
  writeCookie(CONSENT_COOKIE, state, CONSENT_TTL_DAYS);
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(CONSENT_KEY, state);
  }
}

/**
 * Injects Smartlook and Google Ads scripts.
 * Safe to call multiple times — guards against double-injection.
 * Only call this after confirming consent === "accepted".
 */
export function loadTrackingScripts(): void {
  if (typeof window === "undefined") return;

  // ── Smartlook ────────────────────────────────────────────────────────────────
  if (!window.smartlook) {
    (function (d: Document) {
      const o = (window.smartlook = function (...args: unknown[]) {
        (o as unknown as { api: unknown[] }).api.push(args);
      }) as unknown as { api: unknown[] };
      o.api = [];
      const h = d.getElementsByTagName("head")[0];
      const c = d.createElement("script");
      c.async = true;
      c.type = "text/javascript";
      c.charset = "utf-8";
      c.src = "https://web-sdk.smartlook.com/recorder.js";
      h.appendChild(c);
    })(document);
    (window.smartlook as Function)("init", "9d5e7d8c97cb3ae1dd30ddc67aedf75ed506adec", {
      region: "eu",
    });
  }

  // ── Google Ads (AW-17684009574) ───────────────────────────────────────────────
  if (!window.__gtagLoaded) {
    window.__gtagLoaded = true;

    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src =
      "https://www.googletagmanager.com/gtag/js?id=AW-17684009574";
    document.head.appendChild(gtagScript);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
    window.gtag("js", new Date());
    window.gtag("config", "AW-17684009574");

    // Conversion helper used by CTA buttons (overrides the no-op stub in seo.tsx)
    window.gtag_report_conversion = function (url?: string) {
      const callback = function () {
        if (url !== undefined) window.location.href = url;
      };
      window.gtag("event", "conversion", {
        send_to: "AW-17684009574/PnNmCOG3iLUbEOaksvBB",
        value: 1.0,
        currency: "USD",
        event_callback: callback,
      });
      return false;
    };
  }
}
